<template>
    <section class="internal-page">
        <internal-page-header
            eyebrow="Área do credor"
            title="Habilitações e divergências"
            description="Preencha os dados e anexe os documentos da sua solicitação."
            back-to="/habilitacoes-divergencias"
            back-label="Minhas solicitações"
        />
        <v-container class="pa-0 mx-auto">
            <v-row justify="center">
                <v-col cols="10">
                    <div
                        v-html="!user ? dataset.contentUserAnonymous : dataset.contentUserLogged"
                    ></div>
                </v-col>
            </v-row>

            <v-row justify="center">
                <v-col cols="10" lg="10">
                    <v-form class="pt-10" ref="form" v-model="dataForm.validForm">
                        <p class="font-weight-bold">Qual empresa que está devendo para você?</p>

                        <v-row>
                            <v-col cols="12" sm="12">
                                <v-autocomplete
                                    :disabled="dataset.loading"
                                    dense
                                    required
                                    :items="dataset.clients"
                                    label="Selecione a devedora (nome ou CNPJ)"
                                    v-model="dataset.data.client"
                                    item-value="id"
                                    item-text="name"
                                    :filter="filterClient"
                                    :rules="[$rules.required]"
                                    outlined
                                    hide-details="auto"
                                >
                                </v-autocomplete>
                            </v-col>
                        </v-row>

                        <creditor-data-section
                            v-if="!user"
                            :value="creditor"
                            :disabled="dataset.loading"
                            :max-file-size-mb="maxFileSizeMb"
                            @login="openLogin"
                        />
                        <div v-else class="habilitation-section">
                            <p class="habilitation-section__title"><strong>Credor</strong></p>
                            <p class="habilitation-section__hint">
                                Enviando como
                                <strong class="text-capitalize">{{ user.name }}</strong
                                >.
                            </p>
                            <v-select
                                v-model="creditor.creditor_type"
                                :items="creditorProfiles"
                                item-value="id"
                                item-text="name"
                                label="Tipo de credor"
                                :disabled="dataset.loading"
                                :rules="[$rules.required]"
                                outlined
                                dense
                                hide-details="auto"
                            />
                        </div>

                        <representation-section
                            :value="representation"
                            :creditor-type="creditorType"
                            :disabled="dataset.loading"
                            :max-file-size-mb="maxFileSizeMb"
                        />

                        <div class="habilitation-section">
                            <p class="mb-1">
                                <strong
                                    >Você deseja habilitar um novo crédito ou já possui crédito
                                    habilitado e quer apenas retificá-lo?</strong
                                >
                            </p>
                            <v-radio-group
                                v-model="dataset.data.requestType"
                                row
                                :disabled="dataset.loading"
                                :rules="[(answer) => !!answer || 'Campo obrigatório']"
                                hide-details="auto"
                                class="mt-0"
                            >
                                <v-radio label="Habilitar novo crédito" :value="1" />
                                <v-radio label="Retificar crédito existente" :value="2" />
                            </v-radio-group>
                        </div>

                        <v-row>
                            <v-col>
                                <strong>Negócios</strong>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col>
                                <p v-if="!dataset.loading">
                                    Para adicionar um novo negócio,
                                    <a href="#" @click.prevent="addItem"
                                        ><strong>clique aqui</strong></a
                                    >
                                </p>
                            </v-col>
                        </v-row>

                        <div v-for="(item, i) in dataset.data.items" :key="i">
                            <v-row>
                                <v-col>
                                    <strong>Negócio #{{ getItemNumber(i) }}</strong>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <v-expansion-panels>
                                        <v-expansion-panel>
                                            <v-expansion-panel-header>
                                                <div>
                                                    <strong>Origem:</strong>
                                                    <span>{{
                                                        truncateText(item.origin || 'N/A', 10)
                                                    }}</span>
                                                    /
                                                    <strong>Valor:</strong>
                                                    <span v-if="item.amount"
                                                        >{{
                                                            item.currency
                                                                | enum(
                                                                    dataset.currencyTypes,
                                                                    'id',
                                                                    'currencyFormat'
                                                                )
                                                        }}
                                                        {{ item.amount | currency }}</span
                                                    ><span v-else>N/A</span>
                                                </div>
                                            </v-expansion-panel-header>
                                            <v-expansion-panel-content>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <p>
                                                            <strong
                                                                >Especifique a origem do crédito
                                                                (contrato bancário, prestação de
                                                                serviços, notas fiscais
                                                                etc.)</strong
                                                            >
                                                            <v-tooltip bottom>
                                                                <template
                                                                    v-slot:activator="{ on, attrs }"
                                                                >
                                                                    <v-icon
                                                                        small
                                                                        color="primary"
                                                                        v-bind="attrs"
                                                                        v-on="on"
                                                                        >mdi-help-circle</v-icon
                                                                    >
                                                                </template>
                                                                <span
                                                                    >Lembre-se que para cada
                                                                    contrato existente é necessário
                                                                    adicionar um novo negócio.</span
                                                                >
                                                            </v-tooltip>
                                                        </p>
                                                        <v-text-field
                                                            :disabled="dataset.loading"
                                                            dense
                                                            label="Origem do negócio"
                                                            v-model="item.origin"
                                                            required
                                                            :rules="[$rules.required]"
                                                            outlined
                                                            hide-details="auto"
                                                        >
                                                        </v-text-field>
                                                    </v-col>
                                                </v-row>
                                                <v-row>
                                                    <v-col>
                                                        <p>
                                                            <strong
                                                                >Referente a este negócio, qual
                                                                valor você entende que é
                                                                devido?</strong
                                                            >
                                                        </p>
                                                    </v-col>
                                                </v-row>
                                                <v-row no-gutters>
                                                    <v-col cols="12" sm="6">
                                                        <v-select
                                                            :disabled="dataset.loading"
                                                            dense
                                                            id="type"
                                                            :items="dataset.currencyTypes"
                                                            label="Selecione uma moeda"
                                                            v-model="item.currency"
                                                            item-value="id"
                                                            item-text="name"
                                                            outlined
                                                            hide-details="auto"
                                                        >
                                                        </v-select>
                                                    </v-col>
                                                    <v-col cols="12" sm="6">
                                                        <v-currency-field
                                                            :disabled="dataset.loading"
                                                            class="ml-2"
                                                            label="Valor"
                                                            dense
                                                            v-model="item.amount"
                                                            :allowNegative="false"
                                                            hide-details="auto"
                                                            outlined
                                                            required
                                                            :rules="[$rules.required]"
                                                        >
                                                        </v-currency-field>
                                                    </v-col>
                                                </v-row>

                                                <v-row>
                                                    <v-col cols="12">
                                                        <p>
                                                            <strong
                                                                >Selecione abaixo a classe que você
                                                                entende que este crédito deverá ser
                                                                relacionado</strong
                                                            >
                                                        </p>
                                                        <v-select
                                                            :disabled="dataset.loading"
                                                            dense
                                                            :items="creditorTypes"
                                                            label="Classe"
                                                            v-model="item.type"
                                                            item-value="id"
                                                            item-text="name"
                                                            outlined
                                                            hide-details="auto"
                                                            :rules="[$rules.required]"
                                                        >
                                                        </v-select>
                                                    </v-col>
                                                </v-row>

                                                <v-row>
                                                    <v-col cols="12" class="pb-0">
                                                        <p class="mb-1">
                                                            <strong
                                                                >Anexe os documentos do
                                                                negócio</strong
                                                            >
                                                        </p>
                                                        <small class="attachments-hint"
                                                            >PDF, JPG ou PNG, até
                                                            {{ maxFileSizeMb }} MB por
                                                            arquivo.</small
                                                        >
                                                    </v-col>
                                                    <v-col
                                                        v-for="field in documentFields"
                                                        :key="field.key"
                                                        cols="12"
                                                        md="6"
                                                    >
                                                        <v-file-input
                                                            v-model="item.documents[field.key]"
                                                            :disabled="dataset.loading"
                                                            :multiple="field.multiple"
                                                            :label="`${field.label} (${
                                                                field.required
                                                                    ? 'obrigatório'
                                                                    : 'opcional'
                                                            })`"
                                                            :hint="field.hint"
                                                            :rules="fileRules(field)"
                                                            accept="application/pdf, image/png, image/jpeg"
                                                            prepend-icon=""
                                                            prepend-inner-icon="mdi-paperclip"
                                                            persistent-hint
                                                            show-size
                                                            small-chips
                                                            outlined
                                                            dense
                                                            hide-details="auto"
                                                        />
                                                    </v-col>
                                                </v-row>

                                                <v-row>
                                                    <v-col cols="12">
                                                        <p>
                                                            <strong
                                                                >Informe uma observação referente ao
                                                                negócio</strong
                                                            >
                                                        </p>
                                                        <v-textarea
                                                            :disabled="dataset.loading"
                                                            dense
                                                            id="description"
                                                            label="Informações Adicionais"
                                                            :rules="[
                                                                $rules.maxlength(
                                                                    item.observation,
                                                                    500
                                                                )
                                                            ]"
                                                            counter="500"
                                                            v-model="item.observation"
                                                            outlined
                                                            hide-details="auto"
                                                            rows="2"
                                                            auto-grow
                                                        ></v-textarea>
                                                    </v-col>
                                                </v-row>

                                                <v-row>
                                                    <v-col>
                                                        <v-btn
                                                            color="primary"
                                                            text
                                                            :loading="dataset.loading"
                                                            @click.native="removeItem(i)"
                                                        >
                                                            <v-icon> mdi-delete-outline </v-icon>
                                                            Excluir</v-btn
                                                        >
                                                    </v-col>
                                                </v-row>
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>
                                    </v-expansion-panels>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <v-divider></v-divider>
                                </v-col>
                            </v-row>
                        </div>

                        <v-row>
                            <v-col>
                                <v-btn
                                    color="primary"
                                    :loading="dataset.loading"
                                    @click.native="save"
                                    >Salvar</v-btn
                                >
                                <span v-if="uploadStatus" class="ml-4 upload-status">{{
                                    uploadStatus
                                }}</span>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-col>
            </v-row>
        </v-container>
    </section>
</template>

<script>
import 'moment/locale/pt-br';

import { eventHub } from '@/main';

import currencyTypes from '@/common/currency-types.data';
import creditorProfiles, { CREDITOR_PROFILE } from '@/common/creditor-profiles.data';
import string from '@/common/util/string';
import CreditorDataSection from '@/components/habilitation/CreditorDataSection.vue';
import RepresentationSection, {
    emptyRepresentation
} from '@/components/habilitation/RepresentationSection.vue';
import { getCreditorTypes } from '@/common/creditor-types.data.js';

import axios from 'axios';

const MAX_FILE_SIZE_MB = 10;

// Tipos de documento do credor (user_docs.type), os mesmos da Central de Documentos.
const DOCUMENT_FIELDS = [
    {
        key: 'supporting',
        type: 3,
        label: 'Documentos comprobatórios',
        hint: 'Contratos, notas fiscais, títulos e outros documentos que comprovem o crédito.',
        multiple: true,
        required: true
    },
    {
        key: 'petition',
        type: 5,
        label: 'Petição',
        hint: 'Petição de habilitação ou divergência assinada, se houver.',
        multiple: false,
        required: false
    },
    {
        key: 'procuration',
        type: 2,
        label: 'Procuração',
        hint: 'Obrigatória quando o pedido for apresentado por advogado.',
        multiple: false,
        required: false
    },
    {
        key: 'spreadsheet',
        type: 6,
        label: 'Planilha de cálculos',
        hint: 'Memória de cálculo do valor informado.',
        multiple: false,
        required: false
    },
    {
        key: 'others',
        type: 4,
        label: 'Outros documentos',
        hint: 'Qualquer outro documento que ajude na análise do crédito.',
        multiple: true,
        required: false
    }
];

const emptyDocuments = () => ({
    supporting: [],
    petition: null,
    procuration: null,
    spreadsheet: null,
    others: []
});

const asList = (value) => (Array.isArray(value) ? value : value ? [value] : []);

// Tipos de documento (user_docs.type) dos representantes.
const REPRESENTATIVE_DOC_TYPE = {
    procuration: 2,
    legalDocument: 7,
    nomination: 8,
    acceptance: 9,
    identity: 10
};
const REPRESENTATIVE_KIND = { LAWYER: 1, LEGAL: 2, INVENTORY: 3 };

const emptyCreditor = () => ({
    creditor_type: null,
    name: '',
    registration: '',
    email: '',
    document: null,
    terms: false
});

export default {
    components: { CreditorDataSection, RepresentationSection },
    data: () => ({
        user: null,
        creditor: emptyCreditor(),
        representation: emptyRepresentation(),
        creditorProfiles,
        valid: true,
        panel: [0],
        selectedClientId: null,
        dataset: {
            currencyTypes: currencyTypes,
            contentUserAnonymous: '',
            contentUserLogged: '',
            clients: [],
            data: {
                items: [],
                requestType: null
            },
            loading: false
        },
        dataForm: {
            validForm: true,
            addLoading: false,
            updateLoading: false,
            removeLoading: false,
            created: null
        },
        uploadStatus: '',
        maxFileSizeMb: MAX_FILE_SIZE_MB,
        documentFields: DOCUMENT_FIELDS
    }),

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    mounted: function () {},

    watch: {
        creditorTypes(types) {
            const ids = types.map((type) => type.id);
            this.dataset.data.items.forEach((item) => {
                if (item.type && !ids.includes(item.type)) item.type = null;
            });
        }
    },

    computed: {
        selectedClient() {
            return this.dataset.clients.find((client) => client.id === this.dataset.data.client);
        },
        creditorTypes() {
            return getCreditorTypes(this.selectedClient && this.selectedClient.type);
        },
        creditorType() {
            return Number(this.creditor.creditor_type) || null;
        }
    },
    created() {
        this.selectedClientId = this.$route.query.id;
        console.log('id create', this.selectedClientId);
        if (this.$root.user) this.loadUserInfo();
        this.loadClients();

        this.loadText();
    },

    methods: {
        truncateText(text, maxLength) {
            return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
        },

        getItemNumber(i) {
            return i + 1;
        },

        async loadText() {
            try {
                const result = await axios.get(`/api/dynamic-infos/keys`, {
                    params: { keys: 'create-user-event-anonymous,create-user-event-logged' }
                });

                if (!result.data.content) return;

                let contentUserAnonymous = result.data.content.find(
                    (x) => x.key === 'create-user-event-anonymous'
                );
                let contentUserLogged = result.data.content.find(
                    (x) => x.key === 'create-user-event-logged'
                );

                this.dataset.contentUserAnonymous = contentUserAnonymous
                    ? contentUserAnonymous.value
                    : '';
                this.dataset.contentUserLogged = contentUserLogged ? contentUserLogged.value : '';
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                });
            }
        },

        filterClient(client, queryText) {
            const normalize = (value) =>
                String(value || '')
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toLowerCase();
            const query = normalize(queryText).trim();
            const digits = query.replace(/\D/g, '');
            const registration = String(client.registration || '').replace(/\D/g, '');

            return (
                normalize(client.name).includes(query) ||
                (digits.length > 0 && registration.includes(digits))
            );
        },

        async loadClients() {
            try {
                const result = await axios.get(`/api/clients`, {
                    params: { search: JSON.stringify({ event_opened: true }), limit: 100000 }
                });

                if (!result.data.content.data) return;

                this.dataset.clients = result.data.content.data;

                const debtorId = parseInt(this.selectedClientId);
                const selectedClient = this.dataset.clients.find(
                    (client) => client.id === debtorId
                );

                if (selectedClient) {
                    this.dataset.data.client = selectedClient.id;
                }
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                });
            }
        },

        // async loadClients() {
        //     try {
        //         const result = await axios.get(`/api/clients`, {
        //             params: { search: JSON.stringify({ event_opened: true }), limit: 100000 }
        //         });

        //         if (!result.data.content.data) return;

        //         console.log(result.data.content.data);
        //         this.dataset.clients = result.data.content.data;
        //     } catch (error) {
        //         this.$dialog.notify.error(this.$codes(error), {
        //             position: 'top-right',
        //             timeout: 2000
        //         });
        //     }
        // },

        async loadUserInfo() {
            this.dataset.loading = true;

            try {
                const result = await axios.get('/api/users/info');

                this.user = result.data.content;
                if (this.user && this.user.creditor_type)
                    this.creditor.creditor_type = this.user.creditor_type;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 5000
                });
            } finally {
                this.dataset.loading = false;
            }
        },

        async load() {
            this.dataset.loading = true;

            try {
                const result = await axios.get('/api/user-events/request');

                this.data = result.data.content;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 5000
                });
            } finally {
                this.dataset.loading = false;
            }
        },

        validateFormItems() {
            if (!this.dataset.data.items.length) return false;

            return this.dataset.data.items.every(
                (item) =>
                    item.origin &&
                    item.amount &&
                    item.currency &&
                    item.type &&
                    asList(item.documents.supporting).length > 0
            );
        },

        async save() {
            if (!this.$refs.form.validate()) return;

            if (!this.dataset.data.items.length) {
                this.$dialog.notify.error(
                    'Preencha as informações de um negócio antes de continuar',
                    {
                        position: 'top-right',
                        timeout: 2000
                    }
                );
                return;
            }

            if (!this.validateFormItems()) {
                this.$dialog.notify.error('Preencha todos os campos antes de continuar', {
                    position: 'top-right',
                    timeout: 2000
                });
                return;
            }

            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja concluir o formulário?',
                title: 'Finalizar',
                actions: {
                    false: 'Cancelar',
                    true: 'Concluir'
                }
            });

            if (!proceedAction) return;

            this.dataset.loading = true;

            let accountCreated = false;

            try {
                if (!this.user) {
                    this.uploadStatus = 'Criando sua conta...';
                    await this.registerCreditor();
                    accountCreated = true;
                }

                const filesByItem = await this.uploadItemDocuments();
                const representatives = await this.uploadRepresentatives();

                this.uploadStatus = 'Registrando a solicitação...';
                await axios.post(
                    '/api/user-events/request',
                    this.buildPayload(filesByItem, representatives)
                );

                await this.$dialog.confirm({
                    text: accountCreated
                        ? 'Pedido enviado! Enviamos um e-mail para você definir sua senha e acompanhar a análise na Área do Credor.'
                        : 'Informações enviadas com sucesso! Você receberá um e-mail assim que sua solicitação for analisada.',
                    title: 'Sucesso',
                    actions: {
                        true: 'OK'
                    }
                });

                this.dataset.data = { items: [], requestType: null };

                // Recarrega para o cabeçalho refletir a sessão recém-criada.
                await this.$router.replace('/habilitacoes-divergencias');
                if (accountCreated) this.$router.go(0);
            } catch (error) {
                if (this.isDuplicatedAccount(error)) {
                    const login = await this.$dialog.confirm({
                        text: 'Já existe uma conta com este e-mail ou CPF/CNPJ. Entre na sua conta para enviar o pedido.',
                        title: 'Conta existente',
                        actions: { false: 'Fechar', true: 'Entrar' }
                    });
                    if (login) this.openLogin();
                    return;
                }

                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 6000
                });
            } finally {
                this.dataset.loading = false;
                this.uploadStatus = '';
            }
        },

        buildPayload(filesByItem, representatives = []) {
            let payload = {
                client_id: this.dataset.data.client,
                type: this.dataset.data.requestType,
                creditor_type: this.creditorType,
                representatives,
                items: []
            };

            this.dataset.data.items.forEach((item, index) => {
                payload.items.push({
                    currency: item.currency,
                    origin: item.origin,
                    amount: item.amount,
                    observation: item.observation,
                    type: item.type,
                    files: filesByItem[index]
                });
            });

            return payload;
        },

        openLogin() {
            eventHub.$emit('DIALOG-ACCESS-FORM', true);
        },

        isDuplicatedAccount(error) {
            const content = error && error.response && error.response.data.content;
            return !!(content && content.error && Number(content.error.code) === 1012);
        },

        // Cria a conta do credor com os dados do formulário e guarda a sessão devolvida,
        // para que os envios seguintes sejam feitos em nome dele.
        async registerCreditor() {
            const formData = new FormData();
            formData.append('creditor_type', this.creditor.creditor_type);
            formData.append('name', this.creditor.name);
            formData.append('registration', string.sanitizeCpfCnpj(this.creditor.registration));
            formData.append('email', this.creditor.email);
            formData.append('terms', this.creditor.terms ? '1' : '0');
            formData.append('file', this.creditor.document);

            const response = await axios.post('/api/users/quick-register', formData);
            const { token, name } = response.data.content;

            localStorage.setItem('token', JSON.stringify(token));
            localStorage.setItem('user', JSON.stringify({ name }));
            this.$root.user = { name };
            this.user = { name, creditor_type: this.creditorType };
        },

        async uploadDocument(file, type) {
            const formData = new FormData();
            formData.append('name', file.name);
            formData.append('type', type);
            formData.append('file', file);

            const response = await axios.post('/api/user-docs', formData);
            return response.data.content.id;
        },

        // Envia os documentos dos representantes e devolve os dados no formato da API.
        async uploadRepresentatives() {
            const { hasLawyer, lawyer, legal, inventory } = this.representation;
            const person = (data) => ({
                name: data.name,
                registration: data.registration ? string.sanitizeCpfCnpj(data.registration) : null,
                oab: data.oab || null,
                email: data.email,
                phone_number: string.sanitizePhone(data.phone_number)
            });

            const representatives = [];

            if (this.creditorType === CREDITOR_PROFILE.COMPANY) {
                for (const representative of legal) {
                    this.uploadStatus = 'Enviando documentos dos representantes...';
                    representatives.push({
                        kind: REPRESENTATIVE_KIND.LEGAL,
                        ...person(representative),
                        files: [
                            await this.uploadDocument(
                                representative.document,
                                REPRESENTATIVE_DOC_TYPE.legalDocument
                            )
                        ]
                    });
                }
            }

            if (this.creditorType === CREDITOR_PROFILE.ESTATE) {
                this.uploadStatus = 'Enviando documentos do inventariante...';
                representatives.push({
                    kind: REPRESENTATIVE_KIND.INVENTORY,
                    ...person(inventory),
                    files: [
                        await this.uploadDocument(
                            inventory.nomination,
                            REPRESENTATIVE_DOC_TYPE.nomination
                        ),
                        await this.uploadDocument(
                            inventory.acceptance,
                            REPRESENTATIVE_DOC_TYPE.acceptance
                        ),
                        await this.uploadDocument(
                            inventory.identity,
                            REPRESENTATIVE_DOC_TYPE.identity
                        )
                    ]
                });
            }

            if (hasLawyer) {
                this.uploadStatus = 'Enviando a procuração...';
                representatives.push({
                    kind: REPRESENTATIVE_KIND.LAWYER,
                    ...person(lawyer),
                    files: [
                        await this.uploadDocument(
                            lawyer.procuration,
                            REPRESENTATIVE_DOC_TYPE.procuration
                        )
                    ]
                });
            }

            return representatives;
        },

        addItem() {
            this.dataset.data.items.push({
                currency: 1,
                documents: emptyDocuments()
            });
        },

        removeItem(index) {
            this.dataset.data.items.splice(index, 1);
        },

        fileRules(field) {
            const maxBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
            return [
                (value) =>
                    !field.required || asList(value).length > 0 || 'Anexe pelo menos um documento',
                (value) =>
                    asList(value).every((file) => file.size <= maxBytes) ||
                    `Cada arquivo deve ter até ${MAX_FILE_SIZE_MB} MB`
            ];
        },

        // Envia os arquivos de cada negócio para a Central de Documentos do credor e
        // devolve os ids, que vinculam os documentos à habilitação.
        async uploadItemDocuments() {
            const uploads = [];
            this.dataset.data.items.forEach((item, index) => {
                DOCUMENT_FIELDS.forEach((field) => {
                    asList(item.documents[field.key]).forEach((file) =>
                        uploads.push({ index, field, file })
                    );
                });
            });

            const filesByItem = this.dataset.data.items.map(() => []);

            for (const [position, upload] of uploads.entries()) {
                this.uploadStatus = `Enviando documentos (${position + 1} de ${uploads.length})...`;

                filesByItem[upload.index].push(
                    await this.uploadDocument(upload.file, upload.field.type)
                );
            }

            return filesByItem;
        }
    }
};
</script>

<style lang="scss">
.title-page {
    color: var(--v-primary-base);
    font-family: 'Manrope', sans-serif;
    font-size: 40px;
    font-style: normal;
    font-weight: 700;
    line-height: 38px;
}

.section-info-title {
    color: var(--v-primary-base);
    font-family: 'Manrope', sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
}

.section-info-value {
    font-family: 'Manrope', sans-serif;
    font-size: 14px;
    font-style: normal;
}

.habilitation-section {
    margin: 28px 0;
}
.habilitation-section__hint {
    color: var(--pbb-muted, #6b7480);
    font-size: 13px;
}
.habilitation-section__card {
    margin: 12px 0 16px;
    padding: 16px 20px 20px;
    border: 1px solid #e5e1da;
    border-radius: 8px;
    background: #fbfaf8;
}
.habilitation-section__card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}

.attachments-hint,
.upload-status {
    color: var(--pbb-muted, #6b7480);
    font-size: 12px;
}

.custom-modal-title {
    font-size: 20px;

    @media (max-width: 600px) {
        font-size: 14px;
    }
}
</style>
